<script lang="ts">
  // Sidebar — 240 px wide navigation rail.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  //   node IDs 1:200-280.
  //
  // Renders the routes registered in `lib/router.ts` plus a brand
  // block, a WS indicator, and a footer slot. The footer surfaces the
  // authenticated username (per R-SPA-AuthGate) and a Logout button
  // that invalidates the session via `lib/auth.ts`.
  //
  // PR 2 update: UserSwitcher + availableDevUsers are gone (the dev
  // impersonation path was removed by the agent in PR 1). We now
  // subscribe to `authUser` for the current username.

  import { resolveRoute, push, ROUTES, type RouteName } from "../lib/router";
  import { getIcon, type IconName } from "../lib/icons";
  import { getValue } from "../lib/store";
  import { authUser, logout } from "../lib/auth";
  import { createWsClient } from "../lib/ws";

  // Current route — read once on mount, refreshed on `popstate`.
  let current = resolveRoute(window.location.pathname + window.location.search);
  if (typeof window !== "undefined") {
    window.addEventListener("popstate", () => {
      current = resolveRoute(window.location.pathname + window.location.search);
    });
  }

  // WS status — subscribe so the connection dot reflects the live
  // socket state. The subscriber handle is intentionally retained for
  // symmetry with Topbar; Svelte's component lifecycle will tear down
  // the subscription when the component is destroyed.
  let connection: "connecting" | "connected" | "disconnected" = "disconnected";
  let singleton: ReturnType<typeof createWsClient> | null = null;

  if (typeof window !== "undefined") {
    singleton = createWsClient();
    singleton.connect();
    singleton.connectionState.subscribe((next) => {
      connection = next;
    });
  }

  // Current authenticated user. App.svelte only mounts Sidebar when
  // `authState === "authenticated"`, so this is always populated in
  // production. The fallback handles the SSR / first-paint case
  // where the auth store hasn't hydrated yet.
  let currentUser = getValue(authUser)?.user ?? "operator";
  const userUnsub = authUser.subscribe((u) => {
    currentUser = u?.user ?? "operator";
  });

  async function onLogout(): Promise<void> {
    try {
      await logout();
    } catch {
      // Network or server error — best-effort logout. App.svelte's
      // auth interceptor will flip state to unauthenticated on the
      // next 401 regardless, so the user lands on /login either way.
    }
  }

  function go(path: string): void {
    current = resolveRoute(path);
    push(path);
  }

  import { onDestroy } from "svelte";
  onDestroy(() => userUnsub?.());

  function indicatorClass(state: typeof connection): string {
    return `ws-indicator ws-indicator--${state}`;
  }

  function labelForRoute(name: RouteName): string {
    switch (name) {
      case "overview": return "Overview";
      case "host": return "Host";
      case "containers": return "Containers";
      case "container-detail": return "Container detail";
      case "audit": return "Audit";
      case "login": return "Login";
    }
  }

  function iconForRoute(name: RouteName): IconName {
    switch (name) {
      case "overview": return "home";
      case "host": return "host";
      case "containers": return "container";
      case "container-detail": return "container";
      case "audit": return "audit";
      case "login": return "home";
    }
  }

  function pathForRoute(name: RouteName): string {
    switch (name) {
      case "overview": return "/";
      case "host": return "/host";
      case "containers": return "/containers";
      case "container-detail": return "/containers";
      case "audit": return "/audit";
      case "login": return "/login";
    }
  }
</script>

<aside class="sidebar" aria-label="Primary">
  <div class="sidebar__brand">
    <span class="sidebar__brand-mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d={getIcon("home")} />
      </svg>
    </span>
    <span class="sidebar__brand-name">rust-dashboard</span>
    <span class="sidebar__brand-tag">v2</span>
  </div>

  <nav class="sidebar__nav" aria-label="Pages">
    {#each ROUTES as route (route.name)}
      {@const icon = getIcon(iconForRoute(route.name))}
      <button
        type="button"
        class="nav-row"
        aria-current={current.name === route.name ? "page" : undefined}
        data-route={route.name}
        on:click={() => go(pathForRoute(route.name))}
      >
        <svg
          class="nav-row__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d={icon} />
        </svg>
        <span class="nav-row__label">{labelForRoute(route.name)}</span>
      </button>
    {/each}
  </nav>

  <div class="sidebar__footer">
    <span class={indicatorClass(connection)} data-testid="ws-indicator" data-state={connection}>
      <span class="ws-indicator__dot" aria-hidden="true"></span>
      <span>{connection}</span>
    </span>
    <button type="button" class="user-menu" aria-label="Open user menu">
      <span class="user-menu__avatar" aria-hidden="true">{currentUser.charAt(0).toUpperCase()}</span>
      <span class="user-menu__name">{currentUser}</span>
      <svg class="user-menu__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d={getIcon("menu")} />
      </svg>
    </button>
  </div>
</aside>
